import React, {useEffect, useMemo, useState} from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {DataGrid, GridToolbar} from "@mui/x-data-grid";
import {baseUrl, ipAddress, userLocation, userName} from "../Univ.js";
function VaccinationPatient() {
    const [tab, setTab] = useState("record");
    /* ============================================================
       MASTER DATA
       ============================================================ */
    const [schedules, setSchedules] = useState([]);
    const [scheduleDoses, setScheduleDoses] = useState([]);
    const [vaccines, setVaccines] = useState([]);
    const [vaccinations, setVaccinations] = useState([]);
    const [loading, setLoading] = useState(false);
    /* ============================================================
       PATIENT SEARCH
       ============================================================ */
    const [patientSearch, setPatientSearch] = useState("");
    const [patientResults, setPatientResults] = useState([]);
    const [patientSearchLoading, setPatientSearchLoading] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState(null);
    /* ============================================================
       VACCINATION FORM
       ============================================================ */
    const [vaccinationForm, setVaccinationForm] = useState({
        patient: "",
        schedule: "",
        schedule_dose: "",
        vaccine: "",
        dose_number: 1,
        dose_name: "",
        scheduled_date: "",
        administered_date: "",
        status: "SCHEDULED",
        vaccination_site: "",
        route: "",
        dose: "",
        batch_number: "",
        remarks: "",
        addedby: userName,
        ip_add: ipAddress,
        location: userLocation
    });
    /* ============================================================
       LOAD MASTER DATA
       ============================================================ */
    useEffect(() => {
        loadMasterData();
    }, []);
    const loadMasterData = () => {
        setLoading(true);
        Promise.all([axios.get(`${baseUrl}vaccination/schedules/`)
            .then(response => {
                setSchedules(response.data || []);
            })
            .catch(error => {
                console.error("Schedule loading error:", error);
            }), axios.get(`${baseUrl}vaccination/schedule-doses/`)
            .then(response => {
                setScheduleDoses(response.data || []);
            })
            .catch(error => {
                console.error("Schedule dose loading error:", error);
            }), axios.get(`${baseUrl}vaccination/vaccines/`)
            .then(response => {
                setVaccines(response.data || []);
            })
            .catch(error => {
                console.error("Vaccine loading error:", error);
            })]).finally(() => {
            setLoading(false);
        });
    };
    /* ============================================================
       GENERAL PATIENT SEARCH
       API:
       pat-search/
       ============================================================ */
    const searchPatients = (value) => {
        setPatientSearch(value);
        if (!value || value.trim().length < 2) {
            setPatientResults([]);
            return;
        }
        setPatientSearchLoading(true);
        axios.get(`${baseUrl}pat-search/`, {
            params: {
                search: value
            }
        })
            .then(response => {
                const data = response.data || [];
                if (Array.isArray(data)) {
                    setPatientResults(data);
                } else {
                    setPatientResults(data.results || []);
                }
            })
            .catch(error => {
                console.error("Patient search error:", error);
                setPatientResults([]);
            })
            .finally(() => {
                setPatientSearchLoading(false);
            });
    };
    /* ============================================================
       SELECT PATIENT
       ============================================================ */
    const selectPatient = (patient) => {
        console.log("SELECTED PATIENT:", patient);
        setSelectedPatient(patient);
        setPatientSearch(patient.patient || patient.pat_id || "");
        setPatientResults([]);
        setVaccinationForm(prev => ({
            ...prev, patient: patient.id
        }));
        if (patient.id) {
            loadPatientVaccinations(patient.id);
        } else {
            setVaccinations([]);
        }
    };
    /* ============================================================
       CLEAR PATIENT
       ============================================================ */
    const clearPatient = () => {
        setSelectedPatient(null);
        setPatientSearch("");
        setPatientResults([]);
        setVaccinations([]);
        resetVaccinationForm();
    };
    /* ============================================================
       LOAD PATIENT VACCINATION RECORDS
       ============================================================ */
    const loadPatientVaccinations = (patientId) => {
        setLoading(true);
        axios.get(`${baseUrl}vaccination/patient-records/`, {
            params: {
                patient: patientId
            }
        })
            .then(response => {
                const data = response.data || [];
                if (Array.isArray(data)) {
                    setVaccinations(data);
                } else {
                    setVaccinations(data.results || []);
                }
            })
            .catch(error => {
                console.error("Patient vaccination loading error:", error);
                setVaccinations([]);
            })
            .finally(() => {
                setLoading(false);
            });
    };
    /* ============================================================
       VACCINATION FORM CHANGE
       ============================================================ */
    const handleVaccinationChange = (e) => {
        const {
            name, value
        } = e.target;
        setVaccinationForm(prev => ({
            ...prev, [name]: value
        }));
        /* ========================================================
           SCHEDULE SELECTED
           ======================================================== */
        if (name === "schedule") {
            setVaccinationForm(prev => ({
                ...prev,
                schedule: value,
                schedule_dose: "",
                vaccine: "",
                dose_number: 1,
                dose_name: "",
                route: "",
                dose: ""
            }));
        }
        /* ========================================================
           DOSE SELECTED
           ======================================================== */
        if (name === "schedule_dose") {
            const dose = scheduleDoses.find(item => String(item.id) === String(value));
            if (dose) {
                let vaccineId = "";
                if (dose.vaccine && typeof dose.vaccine === "object") {
                    vaccineId = dose.vaccine.id;
                } else {
                    vaccineId = dose.vaccine || "";
                }
                const vaccineData = vaccines.find(item => String(item.id) === String(vaccineId));
                setVaccinationForm(prev => ({
                    ...prev,
                    schedule_dose: value,
                    vaccine: vaccineId,
                    dose_number: dose.dose_number || 1,
                    dose_name: dose.dose_name || "",
                    route: vaccineData?.route || "",
                    dose: vaccineData?.default_dose || ""
                }));
            }
        }
        /* ========================================================
           VACCINE SELECTED
           ======================================================== */
        if (name === "vaccine") {
            const vaccine = vaccines.find(item => String(item.id) === String(value));
            if (vaccine) {
                setVaccinationForm(prev => ({
                    ...prev, vaccine: value, route: vaccine.route || "", dose: vaccine.default_dose || ""
                }));
            }
        }
    };
    /* ============================================================
       SAVE VACCINATION
       ============================================================ */
    const saveVaccination = () => {
        if (!selectedPatient) {
            Swal.fire("Required", "Please select a patient.", "warning");
            return;
        }
        if (!vaccinationForm.schedule) {
            Swal.fire("Required", "Please select a vaccination schedule.", "warning");
            return;
        }
        if (!vaccinationForm.schedule_dose) {
            Swal.fire("Required", "Please select a vaccination dose.", "warning");
            return;
        }
        if (!vaccinationForm.scheduled_date) {
            Swal.fire("Required", "Please select the scheduled date.", "warning");
            return;
        }
        const data = {
            patient: vaccinationForm.patient,
            schedule: vaccinationForm.schedule,
            schedule_dose: vaccinationForm.schedule_dose,
            vaccine: vaccinationForm.vaccine || null,
            dose_number: Number(vaccinationForm.dose_number || 1),
            dose_name: vaccinationForm.dose_name || "",
            scheduled_date: vaccinationForm.scheduled_date || null,
            administered_date: vaccinationForm.administered_date || null,
            status: vaccinationForm.status,
            vaccination_site: vaccinationForm.vaccination_site || "",
            route: vaccinationForm.route || "",
            dose: vaccinationForm.dose || "",
            batch_number: vaccinationForm.batch_number || "",
            remarks: vaccinationForm.remarks || "",
            addedby: userName,
            ip_add: ipAddress,
            location: userLocation
        };
        axios.post(`${baseUrl}vaccination/patient-records/`, data)
            .then(() => {
                Swal.fire("Success", "Patient vaccination record added successfully.", "success");
                resetVaccinationForm();
                if (selectedPatient && selectedPatient.id) {
                    loadPatientVaccinations(selectedPatient.id);
                }
            })
            .catch(error => {
                console.error("Vaccination save error:", error);
                console.error("API ERROR:", error.response?.data);
                Swal.fire("Error", "Unable to save vaccination record.", "error");
            });
    };
    /* ============================================================
       RESET FORM
       ============================================================ */
    const resetVaccinationForm = () => {
        setVaccinationForm({
            patient: selectedPatient ? selectedPatient.id : "",
            schedule: "",
            schedule_dose: "",
            vaccine: "",
            dose_number: 1,
            dose_name: "",
            scheduled_date: "",
            administered_date: "",
            status: "SCHEDULED",
            vaccination_site: "",
            route: "",
            dose: "",
            batch_number: "",
            remarks: "",
            addedby: userName,
            ip_add: ipAddress,
            location: userLocation
        });
    };
    /* ============================================================
       FILTER DOSES BY SCHEDULE
       ============================================================ */
    const filteredDoses = useMemo(() => {
        if (!vaccinationForm.schedule) {
            return [];
        }
        return scheduleDoses.filter(item => {
            let scheduleId = item.schedule;
            if (scheduleId && typeof scheduleId === "object") {
                scheduleId = scheduleId.id;
            }
            return String(scheduleId) === String(vaccinationForm.schedule);
        });
    }, [scheduleDoses, vaccinationForm.schedule]);
    /* ============================================================
       GET VACCINE NAME
       ============================================================ */
    const getVaccineName = (row) => {
        if (row.vaccine && typeof row.vaccine === "object") {
            return (row.vaccine.vaccine_name || "");
        }
        const vaccine = vaccines.find(item => String(item.id) === String(row.vaccine));
        return vaccine ? vaccine.vaccine_name : "";
    };
    /* ============================================================
       GET SCHEDULE NAME
       ============================================================ */
    const getScheduleName = (row) => {
        if (row.schedule && typeof row.schedule === "object") {
            return (row.schedule.schedule_name || "");
        }
        const schedule = schedules.find(item => String(item.id) === String(row.schedule));
        return schedule ? schedule.schedule_name : "";
    };
    /* ============================================================
       VACCINATION GRID COLUMNS
       ============================================================ */
    const vaccinationColumns = useMemo(() => [{
        field: "id", headerName: "ID", width: 70
    }, {
        field: "vaccine_name", headerName: "Vaccine", flex: 1, minWidth: 180, renderCell: (params) => (<span>
            {getVaccineName(params.row)}
        </span>)
    }, {
        field: "schedule_name", headerName: "Schedule", flex: 1, minWidth: 180, renderCell: (params) => (<span>
            {getScheduleName(params.row)}
        </span>)
    }, {
        field: "dose_number", headerName: "Dose", width: 80
    }, {
        field: "dose_name", headerName: "Dose Name", width: 140
    }, {
        field: "scheduled_date", headerName: "Scheduled Date", width: 140
    }, {
        field: "administered_date", headerName: "Administered", width: 140
    }, {
        field: "status", headerName: "Status", width: 120
    }, {
        field: "vaccination_site", headerName: "Site", width: 150
    }, {
        field: "batch_number", headerName: "Batch No.", width: 130
    }, {
        field: "route", headerName: "Route", width: 110
    }, {
        field: "dose", headerName: "Dose", width: 100
    }, {
        field: "remarks", headerName: "Remarks", flex: 1, minWidth: 180
    }], [vaccines, schedules]);
    return (<div className="container-fluid py-3">


        {/* ====================================================
                HEADER
            ==================================================== */}

        <div className="card shadow-sm mb-3">

            <div className="card-body">

                <div className="d-flex justify-content-between align-items-center">

                    <div>

                        <h4 className="mb-1">
                            Patient Vaccination
                        </h4>

                        <small className="text-muted">
                            Patient vaccination scheduling
                            and vaccination history
                        </small>

                    </div>


                    <button
                        className="btn btn-outline-success"
                        onClick={loadMasterData}
                        disabled={loading}
                    >

                        {loading ? "Loading..." : "Refresh"}

                    </button>

                </div>

            </div>

        </div>


        {/* ====================================================
                TABS
            ==================================================== */}

        <div className="card shadow-sm mb-3">

            <div className="card-body p-2">

                <div className="d-flex flex-wrap gap-2">

                    <button
                        className={`btn ${tab === "record" ? "btn-success" : "btn-outline-success"}`}
                        onClick={() => setTab("record")}
                    >
                        Vaccination Record
                    </button>


                    <button
                        className={`btn ${tab === "history" ? "btn-success" : "btn-outline-success"}`}
                        onClick={() => setTab("history")}
                    >
                        Vaccination History
                    </button>

                </div>

            </div>

        </div>


        {/* ====================================================
                GENERAL PATIENT SEARCH
            ==================================================== */}

        <div className="card shadow-sm mb-3">

            <div className="card-header">

                <strong>
                    Patient Search
                </strong>

            </div>


            <div className="card-body">

                <div className="row">

                    <div className="col-md-8">

                        <label className="form-label">
                            Search Patient
                        </label>

                        <input
                            type="text"
                            className="form-control"
                            value={patientSearch}
                            onChange={(e) => searchPatients(e.target.value)}
                            placeholder="Search Patient ID / Name / Mobile / ABHA"
                        />


                        {patientSearchLoading && (<small className="text-muted">
                            Searching patients...
                        </small>)}


                        {patientResults.length > 0 && (<div
                            className="list-group mt-2"
                            style={{
                                maxHeight: "300px", overflowY: "auto"
                            }}
                        >

                            {patientResults.map(patient => (<button
                                type="button"
                                key={patient.id}
                                className="list-group-item list-group-item-action"
                                onClick={() => selectPatient(patient)}
                            >

                                <div className="d-flex justify-content-between">

                                    <strong>
                                        {patient.patient || "Unknown Patient"}
                                    </strong>

                                    <span>
                                                        {patient.pat_id || ""}
                                                    </span>

                                </div>


                                <small className="text-muted">

                                    Mobile:
                                    {" "}
                                    {patient.mobile || "-"}

                                    {" | "}

                                    Gender:
                                    {" "}
                                    {patient.gender || "-"}

                                    {" | "}

                                    Age:
                                    {" "}
                                    {patient.age || "-"}

                                </small>

                            </button>))}

                        </div>)}

                    </div>


                    <div className="col-md-4 d-flex align-items-end">

                        <button
                            className="btn btn-outline-secondary"
                            onClick={clearPatient}
                            disabled={!selectedPatient}
                        >
                            Clear Patient
                        </button>

                    </div>

                </div>

            </div>

        </div>


        {/* ====================================================
                SELECTED PATIENT
            ==================================================== */}

        {selectedPatient && (<div className="card shadow-sm mb-3">

            <div className="card-header">

                <strong>
                    Selected Patient
                </strong>

            </div>


            <div className="card-body">

                <div className="row g-3">


                    <div className="col-md-3">

                        <label className="form-label">
                            Patient ID
                        </label>

                        <input
                            type="text"
                            className="form-control"
                            value={selectedPatient.pat_id || ""}
                            readOnly
                        />

                    </div>


                    <div className="col-md-3">

                        <label className="form-label">
                            Patient Name
                        </label>

                        <input
                            type="text"
                            className="form-control"
                            value={selectedPatient.patient || ""}
                            readOnly
                        />

                    </div>


                    <div className="col-md-2">

                        <label className="form-label">
                            Gender
                        </label>

                        <input
                            type="text"
                            className="form-control"
                            value={selectedPatient.gender || ""}
                            readOnly
                        />

                    </div>


                    <div className="col-md-2">

                        <label className="form-label">
                            Age
                        </label>

                        <input
                            type="text"
                            className="form-control"
                            value={selectedPatient.age || ""}
                            readOnly
                        />

                    </div>


                    <div className="col-md-2">

                        <label className="form-label">
                            ABHA
                        </label>

                        <input
                            type="text"
                            className="form-control"
                            value={selectedPatient.abha || ""}
                            readOnly
                        />

                    </div>

                </div>

            </div>

        </div>)}


        {/* ====================================================
                ADD VACCINATION
            ==================================================== */}

        {tab === "record" && (<>

            <div className="card shadow-sm mb-3">

                <div className="card-header">

                    <strong>
                        Add Vaccination Record
                    </strong>

                </div>


                <div className="card-body">

                    <div className="row g-3">


                        {/* SCHEDULE */}

                        <div className="col-md-4">

                            <label className="form-label">
                                Vaccination Schedule
                            </label>

                            <select
                                name="schedule"
                                className="form-select"
                                value={vaccinationForm.schedule}
                                onChange={handleVaccinationChange}
                                disabled={!selectedPatient}
                            >

                                <option value="">
                                    Select Schedule
                                </option>

                                {schedules.map(schedule => (<option
                                    key={schedule.id}
                                    value={schedule.id}
                                >
                                    {schedule.schedule_name}
                                </option>))}

                            </select>

                        </div>


                        {/* DOSE */}

                        <div className="col-md-4">

                            <label className="form-label">
                                Vaccine Dose
                            </label>

                            <select
                                name="schedule_dose"
                                className="form-select"
                                value={vaccinationForm.schedule_dose}
                                onChange={handleVaccinationChange}
                                disabled={!vaccinationForm.schedule}
                            >

                                <option value="">
                                    Select Dose
                                </option>

                                {filteredDoses.map(dose => (<option
                                    key={dose.id}
                                    value={dose.id}
                                >

                                    {dose.dose_name || `Dose ${dose.dose_number}`}

                                </option>))}

                            </select>

                        </div>


                        {/* VACCINE */}

                        <div className="col-md-4">

                            <label className="form-label">
                                Vaccine
                            </label>

                            <select
                                name="vaccine"
                                className="form-select"
                                value={vaccinationForm.vaccine}
                                onChange={handleVaccinationChange}
                            >

                                <option value="">
                                    Select Vaccine
                                </option>

                                {vaccines.map(vaccine => (<option
                                    key={vaccine.id}
                                    value={vaccine.id}
                                >
                                    {vaccine.vaccine_name}
                                </option>))}

                            </select>

                        </div>


                        {/* DOSE NUMBER */}

                        <div className="col-md-2">

                            <label className="form-label">
                                Dose No.
                            </label>

                            <input
                                type="number"
                                min="1"
                                name="dose_number"
                                className="form-control"
                                value={vaccinationForm.dose_number}
                                onChange={handleVaccinationChange}
                            />

                        </div>


                        {/* DOSE NAME */}

                        <div className="col-md-3">

                            <label className="form-label">
                                Dose Name
                            </label>

                            <input
                                type="text"
                                name="dose_name"
                                className="form-control"
                                value={vaccinationForm.dose_name}
                                onChange={handleVaccinationChange}
                            />

                        </div>


                        {/* ROUTE */}

                        <div className="col-md-3">

                            <label className="form-label">
                                Route
                            </label>

                            <input
                                type="text"
                                name="route"
                                className="form-control"
                                value={vaccinationForm.route}
                                onChange={handleVaccinationChange}
                                placeholder="IM / SC / Oral"
                            />

                        </div>


                        {/* DOSE */}

                        <div className="col-md-2">

                            <label className="form-label">
                                Dose
                            </label>

                            <input
                                type="text"
                                name="dose"
                                className="form-control"
                                value={vaccinationForm.dose}
                                onChange={handleVaccinationChange}
                                placeholder="0.5 ml"
                            />

                        </div>


                        {/* SCHEDULED DATE */}

                        <div className="col-md-3">

                            <label className="form-label">
                                Scheduled Date
                            </label>

                            <input
                                type="date"
                                name="scheduled_date"
                                className="form-control"
                                value={vaccinationForm.scheduled_date}
                                onChange={handleVaccinationChange}
                            />

                        </div>


                        {/* ADMINISTERED DATE */}

                        <div className="col-md-3">

                            <label className="form-label">
                                Administered Date
                            </label>

                            <input
                                type="date"
                                name="administered_date"
                                className="form-control"
                                value={vaccinationForm.administered_date}
                                onChange={handleVaccinationChange}
                            />

                        </div>


                        {/* STATUS */}

                        <div className="col-md-3">

                            <label className="form-label">
                                Status
                            </label>

                            <select
                                name="status"
                                className="form-select"
                                value={vaccinationForm.status}
                                onChange={handleVaccinationChange}
                            >

                                <option value="SCHEDULED">
                                    SCHEDULED
                                </option>

                                <option value="GIVEN">
                                    GIVEN
                                </option>

                                <option value="MISSED">
                                    MISSED
                                </option>

                                <option value="CANCELLED">
                                    CANCELLED
                                </option>

                            </select>

                        </div>


                        {/* SITE */}

                        <div className="col-md-3">

                            <label className="form-label">
                                Vaccination Site
                            </label>

                            <input
                                type="text"
                                name="vaccination_site"
                                className="form-control"
                                value={vaccinationForm.vaccination_site}
                                onChange={handleVaccinationChange}
                                placeholder="Left arm"
                            />

                        </div>


                        {/* BATCH */}

                        <div className="col-md-3">

                            <label className="form-label">
                                Batch Number
                            </label>

                            <input
                                type="text"
                                name="batch_number"
                                className="form-control"
                                value={vaccinationForm.batch_number}
                                onChange={handleVaccinationChange}
                            />

                        </div>


                        {/* REMARKS */}

                        <div className="col-md-6">

                            <label className="form-label">
                                Remarks
                            </label>

                            <input
                                type="text"
                                name="remarks"
                                className="form-control"
                                value={vaccinationForm.remarks}
                                onChange={handleVaccinationChange}
                            />

                        </div>


                        {/* BUTTONS */}

                        <div className="col-12">

                            <button
                                className="btn btn-success me-2"
                                onClick={saveVaccination}
                                disabled={!selectedPatient}
                            >
                                Add Vaccination
                            </button>


                            <button
                                className="btn btn-outline-secondary"
                                onClick={resetVaccinationForm}
                            >
                                Clear
                            </button>

                        </div>

                    </div>

                </div>

            </div>


            {/* =================================================
                        PATIENT HISTORY
                        ================================================= */}

            <div className="card shadow-sm">

                <div className="card-header">

                    <strong>
                        Patient Vaccination History
                    </strong>

                </div>


                <div
                    className="card-body"
                    style={{
                        height: 480
                    }}
                >

                    <DataGrid
                        rows={vaccinations}
                        columns={vaccinationColumns}
                        loading={loading}
                        pageSizeOptions={[10, 25, 50]}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 10, page: 0
                                }
                            }
                        }}
                        slots={{
                            toolbar: GridToolbar
                        }}
                    />

                </div>

            </div>

        </>)}


        {/* ====================================================
                HISTORY TAB
            ==================================================== */}

        {tab === "history" && (<div className="card shadow-sm">

            <div className="card-header">

                <strong>
                    Vaccination History
                </strong>

            </div>


            <div
                className="card-body"
                style={{
                    height: 500
                }}
            >

                <DataGrid
                    rows={vaccinations}
                    columns={vaccinationColumns}
                    loading={loading}
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: 10, page: 0
                            }
                        }
                    }}
                    slots={{
                        toolbar: GridToolbar
                    }}
                />

            </div>

        </div>)}

    </div>);
}
export default VaccinationPatient;
